import { z } from "zod";
import {
  isEmailFormatValid,
  isCharacterTypeValid,
} from "@userFrontend/features/registration/schemas/step1/validation";
import {
  BIYOSHI_JOB_TYPE_ID,
  EYELIST_JOB_TYPE_ID,
  CREDENTIAL_REQUIRED_JOB_TYPE_IDS,
  COSMETOLOGIST_LICENSE_CREDENTIAL_ID,
  SUPERVISOR_COSMETOLOGIST_CREDENTIAL_ID,
  JOB_TYPE_VALIDATION_RULES,
  type JobTypeValidationRules,
} from "./constants";

const NUMERIC_REGEX = /^[0-9]+$/;

export const schema = z
  .object({
    sex: z.string().min(1, "入力してください"),
    birthYear: z.number().min(1, "選択してください"),
    birthMonth: z.number().min(1, "選択してください"),
    birthDay: z.number().min(1, "選択してください"),
    addressPrefecture: z.string().min(1, "選択してください"),
    addressCity: z.string().min(1, "選択してください"),
    jobType: z.string().min(1, "選択してください"),
    experience: z.string().min(1, "選択してください"),
    credentials: z.record(z.string(), z.object({ willFlag: z.boolean().nullable() })),
    monthlyCustomers: z.string().min(1, "入力してください"),
    monthlyNominatedCustomers: z
      .string()
      .min(1, "入力してください")
      .regex(NUMERIC_REGEX, "半角数値で入力してください"),
    averageSpending: z.string().min(1, "入力してください"),
    personalSales: z.string().min(1, "入力してください"),
    mailAddress: z
      .string()
      .min(1, "入力してください")
      .max(100, "100文字以内で入力してください")
      .refine(isEmailFormatValid, {
        message: "正しく入力してください",
      }),
    password: z
      .string()
      .min(1, "入力してください")
      .min(8, "8文字以上で入力してください")
      .refine(isCharacterTypeValid, {
        message: "大文字、小文字、数字、記号の2種類以上を使用してください",
      }),
  })
  .check((ctx) => {
    const { jobType, credentials } = ctx.value;
    const jobTypeValidationRules = JOB_TYPE_VALIDATION_RULES[jobType];

    // 数値フィールドのバリデーションを共通化
    const validateNumericField = (fieldName: keyof JobTypeValidationRules, fieldValue: string) => {
      if (!fieldValue) return;

      if (!NUMERIC_REGEX.test(fieldValue)) {
        ctx.issues.push({
          code: "custom",
          path: [fieldName],
          message: "半角数値で入力してください",
          input: ctx.value,
        });
        return;
      }

      const numericValue = Number(fieldValue);
      const validationRule = jobTypeValidationRules[fieldName];

      if (numericValue < validationRule.min) {
        ctx.issues.push({
          code: "custom",
          path: [fieldName],
          message: `値は${validationRule.min}以上にする必要があります`,
          input: ctx.value,
        });
      }

      if (numericValue > validationRule.max) {
        ctx.issues.push({
          code: "custom",
          path: [fieldName],
          message: `値は${validationRule.max}以下にする必要があります`,
          input: ctx.value,
        });
      }
    };

    // 職種別の数値バリデーション
    if (jobTypeValidationRules) {
      validateNumericField("monthlyCustomers", ctx.value.monthlyCustomers);
      validateNumericField("averageSpending", ctx.value.averageSpending);
      validateNumericField("personalSales", ctx.value.personalSales);
    }

    // 指名客数 > 対応客数のチェック
    const total = Number(ctx.value.monthlyCustomers);
    const nominated = Number(ctx.value.monthlyNominatedCustomers);

    if (!Number.isNaN(total) && !Number.isNaN(nominated) && nominated > total) {
      ctx.issues.push({
        code: "custom",
        path: ["monthlyNominatedCustomers"],
        message: "平均対応客数以下で入力してください",
        input: ctx.value,
      });
    }

    // 資格選択のバリデーション
    const isCredentialRequired = CREDENTIAL_REQUIRED_JOB_TYPE_IDS.includes(
      jobType as (typeof CREDENTIAL_REQUIRED_JOB_TYPE_IDS)[number],
    );

    // willFlagがboolean（true/false）の場合のみ「選択された」と見なす
    const isSelected = (credential: { willFlag: boolean | null } | undefined): boolean =>
      typeof credential?.willFlag === "boolean";

    if (isCredentialRequired && credentials) {
      const cosmetologistLicense = credentials[COSMETOLOGIST_LICENSE_CREDENTIAL_ID];

      // 美容師免許が選択されていない場合のエラー
      if (!isSelected(cosmetologistLicense)) {
        // 職種に応じたエラーメッセージを設定
        let errorMessage = "選択してください";
        if (jobType === BIYOSHI_JOB_TYPE_ID) {
          errorMessage = "美容師には美容師免許の資格が必要です";
        } else if (jobType === EYELIST_JOB_TYPE_ID) {
          errorMessage = "アイリストには美容師免許の資格が必要です";
        }

        ctx.issues.push({
          code: "custom",
          path: ["credentials"],
          message: errorMessage,
          input: ctx.value,
        });
      }
    }

    // 管理美容師を保持している場合、美容師免許を取得済みで選択する必要がある
    const supervisorCosmetologist = credentials[SUPERVISOR_COSMETOLOGIST_CREDENTIAL_ID];
    const cosmetologistLicense = credentials[COSMETOLOGIST_LICENSE_CREDENTIAL_ID];

    if (isSelected(supervisorCosmetologist)) {
      // 管理美容師が選択されているが、美容師免許が選択されていない、または取得予定の場合
      if (!isSelected(cosmetologistLicense) || cosmetologistLicense.willFlag) {
        ctx.issues.push({
          code: "custom",
          path: ["credentials"],
          message: "管理美容師を保持している場合、美容師免許 / 取得済みを選択してください",
          input: ctx.value,
        });
      }
    }
  });

export type FormValues = z.infer<typeof schema>;
